<template>
  <v-container fluid class="pa-6">
    <!-- Header Section -->
    <div class="d-flex align-center justify-space-between mb-6">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">Menus Management</h1>
        <p class="text-body-2 text-medium-emphasis mb-0">Configure and manage your menu offerings</p>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" @click="openModal(null)">
        Add Menu
      </v-btn>
    </div>

    <v-row v-if="menus">
      <v-col v-for="menu in menus" :key="menu.id" cols="12">
        <MenuCard
          :menu="menu"
          @edit-menu="openModal"
          @delete-menu="deleteMenu"
        />
      </v-col>
    </v-row>

    <v-dialog v-model="isModalOpen" max-width="800px">
      <v-card class="pa-2">
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon color="primary" class="mr-2">{{ selectedMenu ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ selectedMenu ? "Edit Menu" : "Add Menu" }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            label="Name"
            v-model="form.name"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            required
          ></v-text-field>
          <v-text-field
            label="Choice Of"
            v-model.number="form.choiceOf"
            type="number"
            variant="outlined"
            density="comfortable"
            class="mb-3"
            required
          ></v-text-field>
          <v-textarea
            label="Taco Bar Includes"
            v-model="form.tacoBarIncludes"
            variant="outlined"
            density="comfortable"
            rows="3"
            class="mb-4"
          ></v-textarea>

          <h3 class="text-subtitle-1 font-weight-bold mb-3">Classic Proteins</h3>
          <v-row>
            <v-col
              v-for="protein in allMenuProteins.filter((item)=> item.type === 'classic')"
              :key="protein.id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-checkbox
                v-model="form.menuProteins"
                :label="protein.name"
                :value="protein.id"
                color="primary"
                hide-details
                density="compact"
              ></v-checkbox>
            </v-col>
          </v-row>
          <h3 class="text-subtitle-1 font-weight-bold mb-3">Premium Proteins</h3>
          <v-row>
            <v-col
              v-for="protein in allMenuProteins.filter((item)=> item.type === 'premium')"
              :key="protein.id"
              cols="12"
              sm="6"
              md="4"
            >
              <v-checkbox
                v-model="form.menuProteins"
                :label="protein.name"
                :value="protein.id"
                color="primary"
                hide-details
                density="compact"
              ></v-checkbox>
            </v-col>
          </v-row>

          <h3 class="text-subtitle-1 font-weight-bold mt-4 mb-3">Delivery Options</h3>
          <v-row v-for="(option, index) in form.deliveryOptions" :key="index" align="center">
            <v-col cols="6">
              <v-text-field v-model="option.name" label="Name" readonly variant="outlined" density="comfortable" hide-details></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="option.price" label="Price" type="number" prefix="$" variant="outlined" density="comfortable" hide-details></v-text-field>
            </v-col>
          </v-row>

          <h3 class="text-subtitle-1 font-weight-bold mt-4 mb-3">Addon Packages</h3>
          <v-autocomplete
            v-model="form.addonPackages"
            :items="allAddonPackages"
            item-title="displayName"
            item-value="id"
            label="Select addon packages"
            variant="outlined"
            density="comfortable"
            multiple
            chips
            closable-chips
            clearable
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeModal">Cancel</v-btn>
          <v-btn color="primary" variant="flat" @click="saveMenu">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { menuService } from "@/services/menuService";
import { addonPackageService } from "@/services/addonPackageService";
import { useNotificationStore } from "@/stores";
import MenuCard from "@/components/MenuCard.vue";

const notificationStore = useNotificationStore();

const menus = ref([]);
const allMenuProteins = ref([]);
const allDeliveryOptions = ref([]);
const allAddonPackages = ref([]);
const isModalOpen = ref(false);
const selectedMenu = ref(null);
const form = reactive({
  id: null,
  name: "",
  choiceOf: 0,
  tacoBarIncludes: "",
  menuProteins: [],
  deliveryOptions: [],
  addonPackages: [],
});

async function fetchMenus() {
  try {
    const data = await menuService.getMenus();
    menus.value = data.data;
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load menus");
  }
}
async function fetchAllMenuProteins() {
  try {
    const response = await menuService.getMenuProteins({ page: 1, limit: 100 });
    allMenuProteins.value = response.data || [];
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load menu proteins");
  }
}
async function fetchAllDeliveryOptions() {
  try {
    const response = await menuService.getDeliveryOptions();
    allDeliveryOptions.value = response.data || [];
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load delivery options");
  }
}
function openModal(menu) {
  selectedMenu.value = menu;
  if (menu) {
    Object.assign(form, {
      ...menu,
      menuProteins: menu.menuProteins.map((protein) => protein.id),
      deliveryOptions: allDeliveryOptions.value.map((opt) => {
        const existing = menu.deliveryOptions.find((d) => d.id === opt.id);
        return { id: opt.id, name: opt.name, price: existing ? existing.price : 0 };
      }),
      addonPackages: (menu.addonPackages || []).map((pkg) => pkg.id),
    });
  } else {
    Object.assign(form, {
      id: null,
      name: "",
      choiceOf: 0,
      tacoBarIncludes: "",
      menuProteins: [],
      deliveryOptions: allDeliveryOptions.value.map((opt) => ({
        id: opt.id,
        name: opt.name,
        price: 0,
      })),
      addonPackages: [],
    });
  }
  isModalOpen.value = true;
}
function closeModal() {
  isModalOpen.value = false;
}
async function saveMenu() {
  try {
    const payload = {
      ...form,
      deliveryOptions: form.deliveryOptions.map((opt) => ({
        id: opt.id,
        price: opt.price,
      })),
      addonPackages: form.addonPackages,
    };
    if (selectedMenu.value) {
      await menuService.updateMenu(selectedMenu.value.id, payload);
      notificationStore.showSuccess("Menu updated successfully");
    } else {
      await menuService.createMenu(payload);
      notificationStore.showSuccess("Menu created successfully");
    }
    fetchMenus();
    closeModal();
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to save menu");
  }
}
async function deleteMenu(id) {
  if (confirm("Are you sure you want to delete this menu?")) {
    try {
      await menuService.deleteMenu(id);
      notificationStore.showSuccess("Menu deleted successfully");
      fetchMenus();
    } catch (error) {
      notificationStore.handleApiError(error, "Failed to delete menu");
    }
  }
}
async function fetchAllAddonPackages() {
  try {
    const response = await addonPackageService.getAddonPackages();
    allAddonPackages.value = (response.data || []).map((pkg) => ({
      id: pkg.id,
      displayName: `${pkg.name} ($${Number(pkg.price_per_person).toFixed(2)}/person)`,
    }));
  } catch (error) {
    notificationStore.handleApiError(error, "Failed to load addon packages");
  }
}
async function initialize() {
  await fetchAllMenuProteins();
  await fetchAllDeliveryOptions();
  await fetchAllAddonPackages();
  await fetchMenus();
}
onMounted(() => {
  initialize();
});
</script>

<style scoped>
/* Styles handled globally */
</style>
